import React, { useState, useRef, useEffect } from 'react';
import { Shield, FileText } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/*
 * Full legal Terms of Service & Privacy modal.
 * Shown ONCE after profile setup. User must scroll through and click "I Agree"
 * before accessing the app. Backend logs acceptance with version + timestamp
 * (terms_acceptance_history collection) for legal protection.
 */
const TermsAcceptanceModal = ({ onAccept, onLogout }) => {
  const [loading, setLoading] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      // Mark "read" once user scrolls within 40px of the bottom.
      const reachedBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
      if (reachedBottom) setScrolledToBottom(true);
    };
    el.addEventListener('scroll', onScroll);
    // If content is short enough that no scroll is needed, mark as read immediately.
    if (el.scrollHeight <= el.clientHeight + 5) setScrolledToBottom(true);
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const handleAccept = async () => {
    if (!agreed || !scrolledToBottom) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/legal/terms/accept`,
        { terms_version: '2.0' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onAccept();
    } catch (err) {
      console.error('Failed to accept terms:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-3"
      data-testid="welcome-modal"
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#FF6600] to-[#FF8533] p-4 sm:p-5 text-center flex-shrink-0">
          <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-2xl flex items-center justify-center mx-auto mb-2">
            <Shield size={28} className="text-white" />
          </div>
          <h2 className="text-lg sm:text-xl font-bold text-white">Terms of Service & Privacy</h2>
          <p className="text-white/90 text-xs sm:text-sm mt-1">
            Please read carefully — you must accept to use NutriFitness AI
          </p>
        </div>

        {/* Scrollable legal content */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-5 sm:px-6 py-5 text-sm text-gray-700 dark:text-gray-200 space-y-4"
          data-testid="terms-scroll-content"
        >
          <div className="flex items-center gap-2 text-[#FF6600] font-semibold pb-2 border-b border-gray-200 dark:border-gray-700">
            <FileText size={16} /> Terms of Use (EULA) — v2.0 · Effective March 26, 2026
          </div>

          <Section n="1" title="Acceptance of Terms">
            By using NutriFitness AI, you agree to be bound by these Terms of Use, our Privacy Policy, and any additional terms applicable to specific services. If you do not agree, do not use the app.
          </Section>
          <Section n="2" title="Service Description">
            NutriFitness AI provides AI-powered nutrition tracking, meal logging, exercise tracking, workout planning, and personalized health recommendations. The service is for general informational purposes only.
          </Section>
          <Section n="3" title="User Accounts">
            You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account. You must provide accurate, current information at registration and keep it updated.
          </Section>
          <Section n="4" title="Subscriptions & Payments">
            <p>NutriFitness AI offers the following auto-renewable subscription options:</p>
            <ul className="list-disc list-inside space-y-1 mt-1 ml-1">
              <li><strong>Premium Monthly</strong> — $9.99/month</li>
              <li><strong>Premium Semi-Annual</strong> — $49.99 / 6 months</li>
              <li><strong>Premium Annual</strong> — $79.99/year</li>
            </ul>
            <p className="mt-2">All premium subscriptions include a 7-day free trial for new subscribers. Trial converts automatically unless cancelled at least 24h before it ends.</p>
          </Section>
          <Section n="5" title="Auto-Renewal & Cancellation">
            Subscriptions automatically renew unless auto-renew is turned off at least 24 hours before the end of the current period. Payment will be charged to your Apple ID, Google Play account, or credit card (web) at confirmation of purchase. You can manage and cancel subscriptions through your device account settings or via Account Settings → Manage Subscription in the app. Any unused portion of a free trial is forfeited when you purchase a subscription.
          </Section>
          <Section n="6" title="Refunds">
            For iOS: refunds must be requested through Apple. For Android: refunds must be requested through Google Play. For web purchases (Stripe): contact <a href="mailto:support@nutrifitness.us" className="text-[#FF6600] underline">support@nutrifitness.us</a> within 7 days of purchase.
          </Section>
          <Section n="7" title="Health & Medical Disclaimer">
            <p><strong>NutriFitness AI is NOT a substitute for professional medical advice, diagnosis, or treatment.</strong> The app provides AI-generated estimates for calorie counting and nutritional information. Always consult a qualified healthcare professional before making any health-related decisions, starting a new diet, or beginning an exercise program — especially if you have any medical condition, are pregnant, or take medication.</p>
            <p className="mt-2">Use the app at your own risk. NutriFitness shall not be liable for any health outcomes resulting from following AI-generated recommendations.</p>
          </Section>
          <Section n="8" title="Privacy & Data Use">
            We collect data you provide (profile, meals, workouts, photos) and limited usage data to operate and improve the service. We do NOT sell your personal data. Photos uploaded for food scanning are processed by AI providers (OpenAI) under their security commitments. Full Privacy Policy: <a href={`${BACKEND_URL}/api/legal/privacy`} target="_blank" rel="noreferrer" className="text-[#FF6600] underline">view here</a>.
          </Section>
          <Section n="9" title="Account Deletion">
            You may delete your account at any time from Account Settings. Upon deletion, all personal data is permanently removed from our servers within 30 days, except where retention is required by law (e.g., financial records).
          </Section>
          <Section n="10" title="Intellectual Property">
            All content, features, and functionality (including AI models, design, brand) are owned by NutriFitness and protected by copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, or create derivative works without written permission.
          </Section>
          <Section n="11" title="Limitation of Liability">
            To the maximum extent permitted by law, NutriFitness shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the app, including but not limited to loss of profits, data, or health outcomes.
          </Section>
          <Section n="12" title="Changes to Terms">
            We reserve the right to modify these terms at any time. Material changes will be communicated by email and an in-app notice, and continued use after notice constitutes acceptance.
          </Section>
          <Section n="13" title="Contact">
            For questions about these Terms or your data, contact <a href="mailto:support@nutrifitness.us" className="text-[#FF6600] underline">support@nutrifitness.us</a>.
          </Section>

          {!scrolledToBottom && (
            <div className="text-center text-xs text-gray-500 dark:text-gray-400 italic pt-2">
              ↓ Please scroll to the bottom to enable acceptance ↓
            </div>
          )}
        </div>

        {/* Checkbox + buttons */}
        <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700 p-4 sm:p-5 space-y-3 bg-white dark:bg-gray-800">
          <label
            className={`flex items-start gap-2 cursor-pointer text-sm select-none ${
              scrolledToBottom ? 'text-gray-700 dark:text-gray-200' : 'text-gray-400 cursor-not-allowed'
            }`}
          >
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              disabled={!scrolledToBottom}
              className="mt-0.5 w-4 h-4 accent-[#FF6600] cursor-pointer disabled:cursor-not-allowed"
              data-testid="terms-agree-checkbox"
            />
            <span>
              I have read and agree to the <strong>Terms of Service</strong> and <strong>Privacy Policy</strong> of NutriFitness AI.
            </span>
          </label>

          <div className="flex gap-2">
            {onLogout && (
              <button
                onClick={onLogout}
                className="flex-1 py-2.5 px-3 border-2 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 font-semibold rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm"
                data-testid="welcome-decline-btn"
              >
                Decline & Log out
              </button>
            )}
            <button
              onClick={handleAccept}
              disabled={loading || !agreed || !scrolledToBottom}
              className="flex-1 py-2.5 px-3 bg-[#28a745] hover:bg-[#218838] text-white font-semibold rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed text-sm"
              data-testid="welcome-agree-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto" />
              ) : (
                'I Agree & Continue'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Section = ({ n, title, children }) => (
  <div>
    <h3 className="font-semibold text-gray-900 dark:text-white text-sm mb-1">
      {n}. {title}
    </h3>
    <div className="leading-relaxed text-[13px] sm:text-sm">{children}</div>
  </div>
);

export default TermsAcceptanceModal;
